<template>
  <div  class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Update Disease Case</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Case Reference -->
          <div class="alert alert-info">
            <h6 class="alert-heading">Case #{{ caseData.surveillance_id || 'N/A' }}</h6>
            <p class="mb-0">
              Patient: <strong>{{ patientFullName }}</strong><br>
              Disease: <strong>{{ caseData.disease_type || 'Unknown' }}</strong>
              <span v-if="isNotifiable" class="badge bg-danger ms-2">Notifiable</span>
            </p>
          </div>

          <!-- Update Form -->
          <form @submit.prevent="updateCase">
            <!-- Disease Information -->
            <div class="card border mb-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Disease Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Disease Type <span class="text-danger">*</span></label>
                    <select v-model="formData.diseaseType" class="form-select" required>
                      <option value="">Select disease</option>
                      <optgroup label="Notifiable Diseases">
                        <option v-for="disease in notifiableDiseases" :key="disease" :value="disease">
                          {{ disease }}
                        </option>
                      </optgroup>
                      <optgroup label="Other Diseases">
                        <option v-for="disease in otherDiseases" :key="disease" :value="disease">
                          {{ disease }}
                        </option>
                      </optgroup>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Onset Date <span class="text-danger">*</span></label>
                    <input v-model="formData.onsetDate" type="date" class="form-control"
                           :max="today" required>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Severity <span class="text-danger">*</span></label>
                    <select v-model="formData.severity" class="form-select" required>
                      <option value="">Select severity</option>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Current Status</label>
                    <select v-model="formData.currentStatus" class="form-select">
                      <option value="active">Active</option>
                      <option value="recovering">Recovering</option>
                      <option value="recovered">Recovered</option>
                      <option value="referred">Referred</option>
                      <option value="deceased">Deceased</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Clinical Information -->
            <div class="card border mb-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Clinical Information</h6>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <label class="form-label">Symptoms <span class="text-danger">*</span></label>
                  <div class="row g-2 mb-2">
                    <div v-for="symptom in commonSymptoms" :key="symptom" class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox"
                               :id="'symptom_' + symptom"
                               :value="symptom" v-model="selectedSymptoms">
                        <label class="form-check-label" :for="'symptom_' + symptom">
                          {{ symptom }}
                        </label>
                      </div>
                    </div>
                  </div>
                  <textarea v-model="formData.symptomsOther" class="form-control mt-2" rows="2"
                            placeholder="Other symptoms or additional details..."></textarea>
                </div>

                <div class="mb-3">
                  <label class="form-label">Treatment Given</label>
                  <textarea v-model="formData.treatmentGiven" class="form-control" rows="2"
                            placeholder="Describe treatment provided..."></textarea>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <div class="form-check">
                      <input v-model="formData.isolationRequired" class="form-check-input"
                             type="checkbox" id="isolationCheck">
                      <label class="form-check-label" for="isolationCheck">
                        Isolation Required
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3">
                    <div class="form-check">
                      <input v-model="formData.hospitalized" class="form-check-input"
                             type="checkbox" id="hospitalizedCheck">
                      <label class="form-check-label" for="hospitalizedCheck">
                        Hospitalized
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="form-label">Contacts Traced</label>
                    <input v-model.number="formData.contactsTraced" type="number"
                           class="form-control" min="0" placeholder="0">
                  </div>
                </div>

                <div v-if="formData.hospitalized" class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Hospital Name</label>
                    <input v-model="formData.hospitalName" type="text" class="form-control"
                           placeholder="Enter hospital name">
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Admission Date</label>
                    <input v-model="formData.admissionDate" type="date" class="form-control">
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Required -->
            <div class="card border mb-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Action Required</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="form-check">
                      <input v-model="formData.reportToWoreda" class="form-check-input"
                             type="checkbox" id="woredaCheck">
                      <label class="form-check-label" for="woredaCheck">
                        Report to Woreda Health Office
                        <span v-if="isNotifiable" class="text-danger">*</span>
                      </label>
                    </div>
                  </div>

                  <div class="col-md-6 mb-3">
                    <div class="form-check">
                      <input v-model="formData.needsFollowUp" class="form-check-input"
                             type="checkbox" id="followUpCheck">
                      <label class="form-check-label" for="followUpCheck">
                        Needs Follow-up Visit
                      </label>
                    </div>
                  </div>
                </div>

                <div v-if="formData.needsFollowUp" class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Follow-up Date</label>
                    <input v-model="formData.followUpDate" type="date" class="form-control"
                           :min="tomorrow">
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Follow-up Priority</label>
                    <select v-model="formData.followUpPriority" class="form-select">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Additional Notes</label>
                  <textarea v-model="formData.additionalNotes" class="form-control" rows="3"
                            placeholder="Any additional observations or actions required..."></textarea>
                </div>
              </div>
            </div>

            <!-- Outcome (if recovered/deceased) -->
            <div v-if="formData.currentStatus === 'recovered' || formData.currentStatus === 'deceased'"
                 class="card border mb-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Case Outcome</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Outcome Date <span class="text-danger">*</span></label>
                    <input v-model="formData.outcomeDate" type="date" class="form-control"
                           :max="today" required>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Final Diagnosis</label>
                    <input v-model="formData.finalDiagnosis" type="text" class="form-control"
                           placeholder="Confirmed diagnosis">
                  </div>
                </div>

                <div v-if="formData.currentStatus === 'deceased'" class="mb-3">
                  <label class="form-label">Cause of Death</label>
                  <textarea v-model="formData.causeOfDeath" class="form-control" rows="2"
                            placeholder="Primary cause of death..."></textarea>
                </div>
              </div>
            </div>

            <!-- Alert Message -->
            <div v-if="showValidationError" class="alert alert-danger" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              {{ validationError }}
            </div>

            <!-- Success Message -->
            <div v-if="showSuccess" class="alert alert-success" role="alert">
              <i class="bx bx-check-circle me-2"></i>
              Case updated successfully!
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button type="button" class="btn btn-primary" @click="updateCase" :disabled="submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Updating...
            </span>
            <span v-else>
              <i class="bx bx-save"></i> Update Case
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "UpdateCaseModal",
  props: {
    case: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      submitting: false,
      showValidationError: false,
      validationError: '',
      showSuccess: false,

      // Form data
      formData: {
        diseaseType: '',
        onsetDate: '',
        severity: '',
        currentStatus: 'active',
        symptomsOther: '',
        treatmentGiven: '',
        isolationRequired: false,
        hospitalized: false,
        contactsTraced: 0,
        hospitalName: '',
        admissionDate: '',
        reportToWoreda: false,
        needsFollowUp: false,
        followUpDate: '',
        followUpPriority: 'medium',
        additionalNotes: '',
        outcomeDate: '',
        finalDiagnosis: '',
        causeOfDeath: ''
      },

      // Selected symptoms
      selectedSymptoms: [],

      // Common symptoms list
      commonSymptoms: [
        'Fever', 'Cough', 'Headache', 'Vomiting', 'Diarrhea', 'Rash',
        'Fatigue', 'Body Aches', 'Sore Throat', 'Difficulty Breathing',
        'Loss of Appetite', 'Abdominal Pain'
      ],

      // Disease lists
      notifiableDiseases: ['AFP', 'Measles', 'Cholera', 'Meningitis', 'Yellow Fever', 'COVID-19', 'Polio', 'Rabies'],
      otherDiseases: ['Malaria', 'Diarrhea', 'Pneumonia', 'Typhoid', 'TB', 'Skin Infection', 'Eye Infection'],

      // Date constraints
      today: new Date().toISOString().split('T')[0],
      tomorrow: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    };
  },

  computed: {
    isNotifiable() {
      return this.notifiableDiseases.includes(this.formData.diseaseType);
    },

    caseData() {
      return this.case || {};
    },

    patientFullName() {
      const firstName = this.caseData.first_name || '';
      const surname = this.caseData.surname || '';
      return `${firstName} ${surname}`.trim() || 'Unknown';
    },

    isReportedToWoreda() {
      return this.caseData.reported_to_woreda === true;
    }
  },

  watch: {
    'formData.diseaseType'(newVal) {
      // Auto-check report to Woreda for notifiable diseases
      if (this.notifiableDiseases.includes(newVal) && !this.isReportedToWoreda) {
        this.formData.reportToWoreda = true;
      }
    },

    'formData.hospitalized'(newVal) {
      if (!newVal) {
        this.formData.hospitalName = '';
        this.formData.admissionDate = '';
      }
    },

    'formData.needsFollowUp'(newVal) {
      if (!newVal) {
        this.formData.followUpDate = '';
        this.formData.followUpPriority = 'medium';
      }
    }
  },

  mounted() {
    this.initializeForm();
  },

  methods: {
    initializeForm() {
      // Pre-fill form with existing case data
      this.formData.diseaseType = this.case.disease_type || '';
      this.formData.onsetDate = this.case.onset_date ? this.case.onset_date.split('T')[0] : '';
      this.formData.severity = this.case.severity || '';
      this.formData.currentStatus = this.case.current_status || 'active';
      this.formData.treatmentGiven = this.case.treatment_given || '';
      this.formData.isolationRequired = this.case.isolation_required || false;
      this.formData.hospitalized = this.case.hospitalized || false;
      this.formData.contactsTraced = this.case.contacts_traced || 0;
      this.formData.additionalNotes = this.case.additional_notes || '';

      // Parse symptoms
      if (this.case.symptoms) {
        const symptoms = this.case.symptoms.split(',').map(s => s.trim());
        this.selectedSymptoms = symptoms.filter(s => this.commonSymptoms.includes(s));
        const otherSymptoms = symptoms.filter(s => !this.commonSymptoms.includes(s));
        this.formData.symptomsOther = otherSymptoms.join(', ');
      }

      // Set report to Woreda if notifiable and not reported
      if (this.isNotifiable && !this.case.reported_to_woreda) {
        this.formData.reportToWoreda = true;
      }
    },

    validateForm() {
      this.showValidationError = false;

      // Required fields
      if (!this.formData.diseaseType) {
        this.validationError = 'Please select a disease type';
        this.showValidationError = true;
        return false;
      }

      if (!this.formData.onsetDate) {
        this.validationError = 'Please enter the onset date';
        this.showValidationError = true;
        return false;
      }

      if (!this.formData.severity) {
        this.validationError = 'Please select severity level';
        this.showValidationError = true;
        return false;
      }

      if (this.selectedSymptoms.length === 0 && !this.formData.symptomsOther) {
        this.validationError = 'Please select or describe symptoms';
        this.showValidationError = true;
        return false;
      }

      // Outcome validation
      if ((this.formData.currentStatus === 'recovered' || this.formData.currentStatus === 'deceased') && !this.formData.outcomeDate) {
        this.validationError = 'Please enter the outcome date';
        this.showValidationError = true;
        return false;
      }

      // Follow-up validation
      if (this.formData.needsFollowUp && !this.formData.followUpDate) {
        this.validationError = 'Please select a follow-up date';
        this.showValidationError = true;
        return false;
      }

      return true;
    },

    async updateCase() {
      if (!this.validateForm()) return;

      this.submitting = true;
      this.showSuccess = false;

      // Combine symptoms
      const allSymptoms = [...this.selectedSymptoms];
      if (this.formData.symptomsOther) {
        allSymptoms.push(...this.formData.symptomsOther.split(',').map(s => s.trim()));
      }

      const data = {
        "function": "updateDiseaseCase",
        "surveillanceId": this.caseData.surveillance_id,
        "diseaseType": this.formData.diseaseType,
        "onsetDate": this.formData.onsetDate,
        "severity": this.formData.severity,
        "currentStatus": this.formData.currentStatus,
        "symptoms": allSymptoms.join(', '),
        "treatmentGiven": this.formData.treatmentGiven,
        "isolationRequired": this.formData.isolationRequired,
        "hospitalized": this.formData.hospitalized,
        "contactsTraced": this.formData.contactsTraced,
        "hospitalName": this.formData.hospitalName,
        "admissionDate": this.formData.admissionDate,
        "reportToWoreda": this.formData.reportToWoreda,
        "needsFollowUp": this.formData.needsFollowUp,
        "followUpDate": this.formData.followUpDate,
        "followUpPriority": this.formData.followUpPriority,
        "additionalNotes": this.formData.additionalNotes,
        "outcomeDate": this.formData.outcomeDate,
        "finalDiagnosis": this.formData.finalDiagnosis,
        "causeOfDeath": this.formData.causeOfDeath
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showSuccess = true;
          setTimeout(() => {
            this.$emit('updated');
            this.$emit('close');
          }, 1500);
        } else {
          this.validationError = res.data.message || 'Failed to update case';
          this.showValidationError = true;
        }
      } catch (e) {
        console.error("Failed to update case:", e);
        this.validationError = 'Failed to update case. Please try again.';
        this.showValidationError = true;
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>